import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './AdminUserRoles.module.css';
import { userApi } from '../../services/userApi';

interface Role {
  Roles_ID: string;
  name: string;
}

interface UserRoles {
  available_roles: Role[];
  assigned_roles: Role[];
}

interface User {
  User_ID: string;
  firstname: string;
  lastname: string;
  title?: string;
  email?: string;
}

const Admin_User_Roles: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [userRoles, setUserRoles] = useState<UserRoles>({
    available_roles: [],
    assigned_roles: []
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  // Add ref to track if component is mounted
  const isMountedRef = useRef<boolean>(true);
  
  // Helper function to extract error message from API response
  const getErrorMessage = async (error: any): Promise<string> => {
    if (error?.response?.json) {
      try {
        const errorData = await error.response.json();
        return errorData.detail || errorData.message || 'An error occurred';
      } catch {
        return error.message || 'An error occurred';
      }
    } else if (error?.detail) {
      return error.detail;
    } else if (error?.message) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    }
    return 'An unexpected error occurred';
  };
  
  // Avoid navigating too quickly which can cause ResizeObserver issues
  const navigateWithDelay = (path: string) => {
    // Short delay to allow cleanup
    setTimeout(() => {
      if (isMountedRef.current) {
        navigate(path);
      }
    }, 50);
  };

  useEffect(() => {
    if (!userId) {
      navigate('/admin/users');
      toast.error('User ID is required', {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        // Fetch user data first
        await fetchUserData();
        // Then fetch role data
        await fetchUserRolesInitial();
        
        toast.success('User data loaded successfully', {
          position: "top-right",
          autoClose: 3000
        });
      } catch (error) {
        console.error('Error loading data:', error);
        const errorMessage = await getErrorMessage(error);
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000
        });
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    loadData();
    
    // Cleanup function to handle unmounting gracefully
    return () => {
      isMountedRef.current = false;
    };
  }, [userId]);
  
  // This version is used for the initial load and shows loading indicator
  const fetchUserRolesInitial = async () => {
    try {
      const rolesData = await userApi.getUserRoles(userId!);
      setUserRoles(rolesData);
    } catch (error) {
      console.error('Error fetching user roles:', error);
      const errorMessage = await getErrorMessage(error);
      
      setError(`Failed to load user roles: ${errorMessage}`);
      toast.error(`Failed to load user roles: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const fetchUserData = async () => {
    try {
      const userData = await userApi.getUserById(userId!);
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
      const errorMessage = await getErrorMessage(error);
      
      setError(`Failed to load user data: ${errorMessage}`);
      toast.error(`Failed to load user data: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const fetchUserRoles = async () => {
    try {
      // Don't set loading to true here as it will cause the UI to show the loading spinner
      // We just want to refresh the data silently
      const rolesData = await userApi.getUserRoles(userId!);
      setUserRoles(rolesData);
    } catch (error) {
      console.error('Error fetching user roles:', error);
      const errorMessage = await getErrorMessage(error);
      
      setError(`Failed to load user roles: ${errorMessage}`);
      toast.error(`Failed to load user roles: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  // Original handler functions (no longer directly used but kept for reference)
  const handleAddRole = () => {
    const availableSelect = document.getElementById('available-roles') as HTMLSelectElement;
    if (!availableSelect || availableSelect.selectedOptions.length === 0) return;
    
    const selectedRoleIds = Array.from(availableSelect.selectedOptions).map(option => option.value);
    
    setUserRoles(prevState => {
      const rolesToAdd = prevState.available_roles.filter(role => 
        selectedRoleIds.includes(String(role.Roles_ID))
      );
      
      const newAvailableRoles = prevState.available_roles.filter(role => 
        !selectedRoleIds.includes(String(role.Roles_ID))
      );
      
      return {
        available_roles: newAvailableRoles,
        assigned_roles: [...prevState.assigned_roles, ...rolesToAdd]
      };
    });
  };

  const handleRemoveRole = () => {
    const assignedSelect = document.getElementById('assigned-roles') as HTMLSelectElement;
    if (!assignedSelect || assignedSelect.selectedOptions.length === 0) return;
    
    const selectedRoleIds = Array.from(assignedSelect.selectedOptions).map(option => option.value);
    
    setUserRoles(prevState => {
      const rolesToRemove = prevState.assigned_roles.filter(role => 
        selectedRoleIds.includes(String(role.Roles_ID))
      );
      
      const newAssignedRoles = prevState.assigned_roles.filter(role => 
        !selectedRoleIds.includes(String(role.Roles_ID))
      );
      
      return {
        available_roles: [...prevState.available_roles, ...rolesToRemove],
        assigned_roles: newAssignedRoles
      };
    });
  };

  const handleAddAllRoles = () => {
    // Only proceed if there are roles to move
    if (userRoles.available_roles.length === 0) {
      toast.info('No available roles to assign', {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }
    
    // Use functional update to ensure we have the latest state
    setUserRoles(prevState => ({
      available_roles: [],
      assigned_roles: [...prevState.assigned_roles, ...prevState.available_roles]
    }));
    
    toast.success('All roles assigned to user', {
      position: "top-right",
      autoClose: 3000
    });
  };

  const handleRemoveAllRoles = () => {
    // Only proceed if there are roles to move
    if (userRoles.assigned_roles.length === 0) {
      toast.info('No assigned roles to remove', {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }
    
    // Use functional update to ensure we have the latest state
    setUserRoles(prevState => ({
      available_roles: [...prevState.available_roles, ...prevState.assigned_roles],
      assigned_roles: []
    }));
    
    toast.success('All roles removed from user', {
      position: "top-right",
      autoClose: 3000
    });
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      
  // Extract role IDs for the API payload as strings
  const roleIds = userRoles.assigned_roles.map(role => role.Roles_ID);
      
  // Save the roles to the server - API expects { roles: string[] }
      await userApi.updateUserRoles(userId!, { roles: roleIds });
      
      // Create a descriptive success message
      const userName = user ? `${user.firstname} ${user.lastname}` : 'User';
      const roleCount = userRoles.assigned_roles.length;
      const successMessage = `${userName} now has ${roleCount} ${roleCount === 1 ? 'role' : 'roles'} assigned`;
      
      // Show success message
      toast.success(successMessage, {
        position: "top-right",
        autoClose: 3000
      });
      
      // Silently refresh the roles data to ensure UI is in sync with backend
      // Using the non-loading version to avoid UI flicker
      await fetchUserRoles();
    } catch (error) {
      console.error('Error updating user roles:', error);
      let errorMessage = 'Failed to update user roles. Please try again.';
      
      if (error instanceof Error) {
        errorMessage = `Error: ${error.message}`;
      }
      
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className={styles.loadingContainer}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className={styles.loadingText}>Loading user roles...</p>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="container mt-5">
        <div className={`alert alert-danger d-flex align-items-center ${styles.errorAlert}`} role="alert">
          <i className={`bi bi-exclamation-triangle-fill me-2 fs-4 ${styles.errorIcon}`}></i>
          <div>
            {error}
            <button 
              type="button" 
              className="btn-close float-end" 
              onClick={() => setError(null)}
              aria-label="Close"
            ></button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
        <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className={`breadcrumb`}>
            <li className={`breadcrumb-item`}>
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className={`breadcrumb-item `}>
              <Link to="/admin/users">User Management</Link>
            </li>
            <li className={`breadcrumb-item `}>
              <Link to={`/admin/users/${userId}`}>{user?.title} {user?.firstname} {user?.lastname}</Link>
            </li>
            <li className={`breadcrumb-item active `} aria-current="page">Roles Management</li>
          </ol>
        </nav>

        {error && (
          <div className={`alert alert-danger ${styles.errorAlert}`} role="alert">
            <i className={`bi bi-exclamation-triangle-fill me-2 ${styles.errorIcon}`}></i>
            {error}
            <button type="button" className="btn-close float-end" onClick={() => setError(null)}></button>
          </div>
        )}

        <div className={`card ${styles.roleContainer}`}>
          <div className={`d-flex align-items-center mb-3 ${styles.roleHeader}`}>
            <i className={`bi bi-shield-lock fs-4 me-2 ${styles.roleHeaderIcon}`}></i>
            <h4 className={`mb-0 ${styles.roleHeaderTitle}`}>Manage Roles for {user?.firstname} {user?.lastname}</h4>
          </div>
          <hr />

          <form id="userForm" data-user-id={userId}>
            <div className="row">
              {/* Available Roles */}
              <div className="col-md-5">
                <h5 className={styles.roleListTitle}>Available Roles</h5>
                <select id="available-roles" className={`form-select ${styles.roleList}`} multiple size={10}>
                  {userRoles.available_roles.map(role => (
                    <option key={role.Roles_ID} value={role.Roles_ID}>{role.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Buttons for moving roles */}
              <div className={`col-md-2 ${styles.buttonContainer}`}>
                <button 
                  type="button" 
                  className={`btn btn-primary ${styles.buttonMove}`} 
                  id="btn-add"
                  onClick={() => {
                    // Get the available select element
                    const availableSelect = document.getElementById('available-roles') as HTMLSelectElement;
                    if (!availableSelect || availableSelect.selectedOptions.length === 0) {
                      toast.info('Please select roles to assign', {
                        position: "top-right",
                        autoClose: 3000
                      });
                      return;
                    }
                    
                    // Get the selected options and their IDs
                    const selectedOptions = Array.from(availableSelect.selectedOptions);
                    const selectedRoleIds = selectedOptions.map(option => option.value);
                    const roleNames = selectedOptions.map(option => option.text);
                    
                    // Update state using functional update
                    setUserRoles(currentRoles => {
                      // Find the roles to move (using string comparison)
                      const rolesToMove = currentRoles.available_roles.filter(role => 
                        selectedRoleIds.includes(String(role.Roles_ID))
                      );
                      
                      // Create new arrays for the updated state
                      const newAvailableRoles = currentRoles.available_roles.filter(role => 
                        !selectedRoleIds.includes(String(role.Roles_ID))
                      );
                      
                      return {
                        available_roles: newAvailableRoles,
                        assigned_roles: [...currentRoles.assigned_roles, ...rolesToMove]
                      };
                    });
                    
                    // Show notification
                    const message = selectedOptions.length === 1 
                      ? `Role '${roleNames[0]}' assigned to user`
                      : `${selectedOptions.length} roles assigned to user`;
                    toast.success(message, {
                      position: "top-right",
                      autoClose: 3000
                    });
                  }}
                >
                  <i className="bi bi-arrow-right"></i>
                </button>
                <button 
                  type="button" 
                  className={`btn btn-primary ${styles.buttonMove}`} 
                  id="btn-remove"
                  onClick={() => {
                    // Get the assigned select element
                    const assignedSelect = document.getElementById('assigned-roles') as HTMLSelectElement;
                    if (!assignedSelect || assignedSelect.selectedOptions.length === 0) {
                      toast.info('Please select roles to remove', {
                        position: "top-right",
                        autoClose: 3000
                      });
                      return;
                    }
                    
                    // Get the selected options and their IDs
                    const selectedOptions = Array.from(assignedSelect.selectedOptions);
                    const selectedRoleIds = selectedOptions.map(option => option.value);
                    const roleNames = selectedOptions.map(option => option.text);
                    
                    // Update state using functional update
                    setUserRoles(currentRoles => {
                      // Find the roles to move (using string comparison)
                      const rolesToMove = currentRoles.assigned_roles.filter(role => 
                        selectedRoleIds.includes(String(role.Roles_ID))
                      );
                      
                      // Create new arrays for the updated state
                      const newAssignedRoles = currentRoles.assigned_roles.filter(role => 
                        !selectedRoleIds.includes(String(role.Roles_ID))
                      );
                      
                      return {
                        available_roles: [...currentRoles.available_roles, ...rolesToMove],
                        assigned_roles: newAssignedRoles
                      };
                    });
                    
                    // Show notification
                    const message = selectedOptions.length === 1 
                      ? `Role '${roleNames[0]}' removed from user`
                      : `${selectedOptions.length} roles removed from user`;
                    toast.success(message, {
                      position: "top-right",
                      autoClose: 3000
                    });
                  }}
                >
                  <i className="bi bi-arrow-left"></i>
                </button>
                <button 
                  type="button" 
                  className={`btn btn-secondary ${styles.buttonMove}`} 
                  id="btn-add-all"
                  onClick={handleAddAllRoles}
                >
                  <i className="bi bi-arrow-right-square-fill"></i>
                </button>
                <button 
                  type="button" 
                  className={`btn btn-secondary ${styles.buttonMove}`} 
                  id="btn-remove-all"
                  onClick={handleRemoveAllRoles}
                >
                  <i className="bi bi-arrow-left-square-fill"></i>
                </button>
              </div>
              
              {/* Assigned Roles */}
              <div className="col-md-5">
                <h5 className={styles.roleListTitle}>Assigned Roles</h5>
                <select id="assigned-roles" className={`form-select ${styles.roleList}`} multiple size={10}>
                  {userRoles.assigned_roles.map(role => (
                    <option key={role.Roles_ID} value={role.Roles_ID}>{role.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Save Button */}
            <div className={styles.saveButtonContainer}>
              <button 
                id="saveButton" 
                className={`btn btn-success ${styles.saveButton}`} 
                type="button"
                onClick={handleSaveChanges}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className={`spinner-border spinner-border-sm ${styles.savingSpinner}`} role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        limit={3}
      />
    </div>
  );
};

export default Admin_User_Roles;
